import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Topbar from "../../components/Topbar/Topbar";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import Horizontalhotelcard from "../../components/Horizontalhotelcard/Horizontalhotelcard";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Pagination from "@mui/material/Pagination";
import { Alert, CircularProgress } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { FaFilter, FaTimes, FaSearch } from 'react-icons/fa';

import "./hotels.css";
import API from "../../API";

function valuetext(value) {
  return `${value} DA`;
}

const PropertyType = {
  HOTEL: "HOTEL",
  DORMITORY: "DORMITORY",
  HOUSE: "HOUSE"
};

export default function Hotels() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Pre-fill the search box from the URL (?location=…&name=…) so the
  // hero search bar on the home page can deep-link into a filtered list.
  const [searchParams] = useSearchParams();
  const initialLocation = searchParams.get("location") || "";
  const initialName = searchParams.get("name") || "";

  // State for filters and search
  const [location, setLocation] = useState(initialLocation);
  const [hotelName, setHotelName] = useState(initialName);
  const [debouncedLocation, setDebouncedLocation] = useState(initialLocation);
  const [debouncedHotelName, setDebouncedHotelName] = useState(initialName);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [committedPriceRange, setCommittedPriceRange] = useState([0, 50000]);
  const [propertyType, setPropertyType] = useState("");
  const [properties, setProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Debounce text inputs (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLocation(location);
      setDebouncedHotelName(hotelName);
    }, 500);
    return () => clearTimeout(timer);
  }, [location, hotelName]);

  // Sync URL → state when the user navigates here with a fresh
  // ?location= or ?name= (e.g. clicking the home hero search again).
  // Keyed off the searchParams string so internal typing doesn't loop.
  const searchParamsKey = searchParams.toString();
  useEffect(() => {
    const params = new URLSearchParams(searchParamsKey);
    const urlLocation = params.get("location") || "";
    const urlName = params.get("name") || "";
    setLocation((prev) => (prev === urlLocation ? prev : urlLocation));
    setHotelName((prev) => (prev === urlName ? prev : urlName));
  }, [searchParamsKey]);

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const searchProperties = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await API.get('/properties/prop/search', {
        params: {
          location: debouncedLocation,
          name: debouncedHotelName,
          minPrice: committedPriceRange[0],
          maxPrice: committedPriceRange[1],
          type: propertyType,
          page: currentPage,
          pageSize
        }
      });
      const { properties: fetchedProperties = [], total = 0 } = response.data;
      setProperties(fetchedProperties);
      setTotalProperties(total);
    } catch (error) {
      setError(error.response?.data?.message || error.message || t("hotels.error"));
      setProperties([]);
      setTotalProperties(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedLocation, debouncedHotelName, committedPriceRange, propertyType, pageSize, t]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    searchProperties();
  }, [searchProperties]);

  const filterVariants = {
    hidden: { opacity: 0, x: isRTL ? 50 : -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        type: "spring",
        stiffness: 100,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        type: "spring",
        stiffness: 120,
      },
    },
  };

  return (
    <div className={`Page_hotels ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Topbar />
      <Navbar page={"hotels"}/>

      <div className="hotels-container">
        {/* Mobile Filter Toggle (inline, above filters/results) */}
        <button
          className="mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
        >
          {mobileFiltersOpen ? <FaTimes /> : <FaFilter />}
          {mobileFiltersOpen ? t("hotels.close_filters") : t("hotels.open_filters")}
        </button>

        {/* Filters Section */}
        <motion.div
          className={`filters-section ${mobileFiltersOpen ? 'mobile-open' : ''}`}
          initial="hidden"
          animate="visible"
          variants={filterVariants}
        >
          <div className="filters-header">
            <h3>{t("hotels.filters")}</h3>
          </div>
          
          <div className="filter-group">
            <label>{t("hotels.location")}</label>
            <input 
              type="text" 
              placeholder={t("hotels.location_placeholder")}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>{t("hotels.hotel_name")}</label>
            <input 
              type="text" 
              placeholder={t("hotels.hotel_name_placeholder")}
              value={hotelName}
              onChange={(e) => setHotelName(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>{t("hotels.property_type")}</label>
            <select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              className="filter-select"
            >
              <option value="">{t("hotels.property_type_all")}</option>
              <option value={PropertyType.HOTEL}>{t("hotels.property_type_hotel")}</option>
              <option value={PropertyType.DORMITORY}>{t("hotels.property_type_dormitory")}</option>
              <option value={PropertyType.HOUSE}>{t("hotels.property_type_house")}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t("hotels.budget")}</label>
            <Box sx={{ width: "100%" }}>
              <Slider
                min={0}
                max={50000}
                step={1000}
                value={priceRange}
                onChange={handlePriceChange}
                onChangeCommitted={(event, newValue) => setCommittedPriceRange(newValue)}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v.toLocaleString()} DA`}
                getAriaValueText={valuetext}
                className="price-slider"
              />
            </Box>
            <div className="price-range-display">
              <span>{priceRange[0].toLocaleString()} DA</span>
              <span>-</span>
              <span>{priceRange[1].toLocaleString()} DA</span>
            </div>
          </div>

          <button 
            className="search-button"
            onClick={searchProperties}
          >
            <FaSearch /> {t("hotels.search_button")}
          </button>
        </motion.div>

        {/* Results Section */}
        <div className="results-section">
          <h2 className="results-title">
            {t("hotels.results_title")} "{location || hotelName || propertyType || t("hotels.property_type_all")}"
          </h2>

          {isLoading && (
            <div className="loading-indicator">
              <CircularProgress />
            </div>
          )}

          {error && (
            <Alert severity="error" className="error-alert">
              {error}
            </Alert>
          )}

          <AnimatePresence>
            {!isLoading && !error && properties.length > 0 && (
              <div className="properties-list">
                {properties.map(property => (
                  <motion.div
                    key={property.id}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <Horizontalhotelcard property={property} />
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>

          {!isLoading && !error && properties.length === 0 && (
            <div className="no-results">
              <h3>{t("hotels.no_results")}</h3>
              <p>{t("hotels.try_different_filters")}</p>
            </div>
          )}

          {!isLoading && !error && properties.length > 0 && (
            <div className="pagination-container">
              <Pagination 
                count={Math.ceil(totalProperties / pageSize)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
              />
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}